import { useMemo } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/base/Seo";
import PageHeader from "@/components/feature/PageHeader";
import DonationForm from "./components/DonationForm";
import Impact from "./components/Impact";
import Faq from "./components/Faq";
import { useDonationConfig } from "@/hooks/useDonationConfig";
import { useCollection } from "@/hooks/useCollection";
import type { FaqItem } from "@/lib/types";

const donHeroImage =
  "https://readdy.ai/api/search-image?query=Warm%20editorial%20wide%20photograph%20of%20a%20diverse%20group%20of%20smiling%20volunteers%20in%20red%20vests%20standing%20together%20holding%20donation%20boxes%20full%20of%20food%20and%20supplies%20in%20a%20bright%20community%20center%2C%20hopeful%20generous%20atmosphere%20of%20solidarity%20and%20charity%2C%20soft%20natural%20light%2C%20premium%20humanitarian%20photography%2C%20high%20detail%2C%20simple%20clean%20background&width=1600&height=500&seq=humanity59-don-hero&orientation=landscape";

export default function Don() {
  const { amounts, trustPoints, impactItems } = useDonationConfig();
  const { items: faqItems } = useCollection<FaqItem>("faq_items");

  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    }),
    [faqItems]
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="Faire un don | Humanity59"
        description="Soutenez Humanity59 par un don unique ou mensuel : financez l'aide alimentaire et la solidarité internationale. Reçu fiscal, don déductible à 66%."
        keywords="faire un don, don déductible, aide alimentaire, solidarité, reçu fiscal"
        path="/don"
        jsonLd={faqItems.length > 0 ? faqJsonLd : undefined}
      />
      <Header />
      <main className="flex-1">
        <PageHeader
          title="Faire un don"
          subtitle="Votre générosité change des vies. Grâce à vous, nous apportons chaque jour une aide concrète aux plus démunis en Afrique et en Asie."
          image={donHeroImage}
        />

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12">
              <div className="lg:py-6">
                <span className="inline-block text-sm font-semibold text-primary-600">
                  Soutenez notre action
                </span>
                <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  Chaque euro compte, chaque don a un impact réel.
                </h2>
                <p className="mt-5 text-base text-foreground-600 leading-relaxed">
                  En faisant un don à Humanity59, vous rejoignez une chaîne de
                  solidarité qui apporte des repas, des vêtements et un soutien
                  direct aux personnes les plus démunies. Votre générosité se
                  transforme en actions concrètes, chaque jour, sur le terrain.
                </p>

                {trustPoints.length > 0 && (
                  <div className="mt-8 space-y-5">
                    {trustPoints.map((point) => (
                      <div key={point.title} className="flex items-start gap-4">
                        <span className="w-12 h-12 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                          <i className={`${point.icon} text-xl`}></i>
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">
                            {point.title}
                          </p>
                          <p className="mt-1 text-sm text-foreground-600">
                            {point.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DonationForm amounts={amounts} />
            </div>
          </div>
        </section>

        <Impact items={impactItems} />
        <Faq items={faqItems} />
      </main>
      <Footer />
    </div>
  );
}